// bootstrapCache — single-flight + short-TTL cache for the uiux runtime
// bootstrap payload (GET /api/uiux-runtime/bootstrap).
//
// Why: on one post-login entry the mount probe (liveMountGate), the
// workspaceGates collection descriptor and the runtime shell each fetched
// the SAME bootstrap endpoint separately (5 fetches observed in production),
// and the tiny gates call queued behind the heavy hydration fetches on the
// browser's per-origin connection limit — users stared at the gate-checking
// panel for ~15-20s. Sharing one in-flight promise and reusing a fresh
// result lets the provider seed gates synchronously from the probe's fetch.
//
// Contract:
// - fetchBootstrapPayload({ client }) resolves the RAW payload; consumers
//   extract their own shapes. Failures REJECT and are NEVER cached, so a
//   transient error does not poison later attempts.
// - readCachedBootstrapPayload() is the synchronous seed path: the last
//   successful payload while fresh (TTL 30s), else null.

export const BOOTSTRAP_CACHE_TTL_MS = 30_000;
export const BOOTSTRAP_PATH = '/api/uiux-runtime/bootstrap';

let inFlight = null;
let cachedPayload = null;
let cachedAtMs = 0;

export const readCachedBootstrapPayload = (nowMs = Date.now()) => (
  cachedPayload !== null && nowMs - cachedAtMs < BOOTSTRAP_CACHE_TTL_MS
    ? cachedPayload
    : null
);

export const fetchBootstrapPayload = ({ client, now = () => Date.now() }) => {
  const fresh = readCachedBootstrapPayload(now());
  if (fresh !== null) return Promise.resolve(fresh);
  if (!inFlight) {
    inFlight = Promise.resolve()
      .then(() => client.request(BOOTSTRAP_PATH, { method: 'GET' }))
      .then((payload) => {
        inFlight = null;
        cachedPayload = payload;
        cachedAtMs = now();
        return payload;
      }, (error) => {
        inFlight = null;
        throw error;
      });
  }
  return inFlight;
};

// Test/logout hygiene: callers that swap identities or assert failure paths
// must clear the module cache between scenarios.
export const clearBootstrapCache = () => {
  inFlight = null;
  cachedPayload = null;
  cachedAtMs = 0;
};
